import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  model,
  signal
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { UmssIconComponent } from '../icon/icon.component';

@Component({
  selector: 'umss-star-review',
  standalone: true,
  imports: [UmssIconComponent],
  templateUrl: './star-review.component.html',
  styleUrls: ['./star-review.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssStarReviewComponent),
      multi: true
    }
  ]
})
export class UmssStarReviewComponent implements ControlValueAccessor {
  amount = input<number>(5);
  highlightedIcon = input<string>('phosphorStarFill');
  icon = input<string>('phStar');
  size = input<'Mini' | 'Small' | 'Regular' | 'Large'>('Regular');
  highlightedColor = input<string>('var(--yellow-400)');
  defaultColor = input<string>('transparent');
  readonly = input<boolean>(false);
  rating = model<number>(0);

  protected isDisabled = signal(false);
  protected hovered = signal<number>(0);

  protected icons = computed(() =>
    Array.from({ length: this.amount() }, (_, i) => i + 1)
  );

  protected iconSize = computed(() => {
    switch (this.size()) {
      case 'Mini':
        return '14px';
      case 'Small':
        return '16px';
      case 'Regular':
        return '20px';
      case 'Large':
        return '24px';
    }
  });

  // CVA callbacks
  private onChange: (value: number) => void = (value: number) => value;
  private onTouched: () => void = () => undefined;

  // ControlValueAccessor
  writeValue(value: number): void {
    this.rating.set(value ?? 0);
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  protected colorFor(index: number): string {
    const threshold = this.hovered() || this.rating();
    return index <= threshold ? this.highlightedColor() : this.defaultColor();
  }

  protected iconFor(index: number): string {
    const threshold = this.hovered() || this.rating();
    return index <= threshold ? this.highlightedIcon() : this.icon();
  }

  protected onHover(index: number): void {
    if (this.isDisabled()) return;
    this.hovered.set(index);
  }

  protected onLeave(): void {
    this.hovered.set(0);
  }

  protected onSelect(index: number): void {
    if (this.isDisabled()) return;
    this.rating.set(index);
    this.onChange(index);
    this.onTouched();
  }
}
